/**
 * ENG-5684 — inline red-flag indicator, the live twin of the prototype's
 * `FulfillmentPassiveFeed/RedFlagDots`.
 *
 * The prototype drew three classes; ENG-5438 ships six. The three new colours
 * extend the existing scale rather than restate it: red and yellow already mean
 * "the work or the money is wrong right now" and purple "someone owes a
 * decision", so a silent expert takes orange (a person has gone quiet), a
 * failed billing period takes rose (money, but a settled fault rather than a
 * live dispute), and an unjoined OF channel takes blue (nobody is at fault; we
 * owe a reply).
 *
 * Order comes from the view model, which pins the api's stable display order,
 * so two rows raising the same classes always draw the same dots in the same
 * places.
 */
import { EngagementRedFlag } from '@/__generated__/graphql';

import type { StaffRedFlag } from './staffFeedViewModel';
import { STAFF_RED_FLAG_LABELS } from './staffFeedViewModel';

const DOT_BASE = 'inline-block h-8 w-8 shrink-0 rounded-full';

/**
 * Every value here must come from a scale that EXISTS in the palette.
 * `tailwind.config.ts` sets `theme.colors` rather than `theme.extend.colors`,
 * so `src/theme/colors.ts` REPLACES Tailwind's defaults: the only hue scales
 * are red, yellow, purple, green, teal and gray. `bg-orange-*`, `bg-rose-*`
 * and `bg-blue-*` compile to nothing and render as transparent circles — which
 * is how the first cut of this file shipped three invisible dots while the
 * chip's count still claimed them, `billing_failed` among them.
 */
const DOT_COLOR: Record<StaffRedFlag, string> = {
  [EngagementRedFlag.HoursEscalated]: 'bg-red-500',
  [EngagementRedFlag.NteThreshold]: 'bg-yellow-600 dark:bg-yellow-500',
  [EngagementRedFlag.GovernanceStale]: 'bg-purple-500',
  [EngagementRedFlag.GhostExpert]: 'bg-gray-500',
  // The pill behind the dots flips (red-50 / red-900 tint), so each dot needs a
  // shade that reads on both.
  [EngagementRedFlag.BillingFailed]: 'bg-red-800 dark:bg-red-400',
  [EngagementRedFlag.UnansweredOfChannel]: 'bg-teal-700',
};

interface StaffRedFlagDotsProps {
  flags: StaffRedFlag[];
  /**
   * Named in the tooltip rather than folded into the flag: a config fault
   * (`pricing_config_invalid` and friends) has the same consequence as a credit
   * shortfall — work confirmed and never billed — but notifies nobody, so an
   * operator needs to see which one they are looking at.
   */
  billingFailureReason?: string | null;
}

export default function StaffRedFlagDots({
  flags,
  billingFailureReason,
}: StaffRedFlagDotsProps): JSX.Element | null {
  // Nothing raised: render nothing, so the cell collapses instead of holding an
  // empty chip on the great majority of rows.
  if (flags.length === 0) return null;

  const labels = flags.map((flag) =>
    flag === EngagementRedFlag.BillingFailed && billingFailureReason
      ? `${STAFF_RED_FLAG_LABELS[flag]} (${billingFailureReason})`
      : STAFF_RED_FLAG_LABELS[flag]
  );

  return (
    <span
      className="inline-flex items-center gap-4 rounded-full border border-red-700/40 bg-red-50 px-8 py-2 dark:bg-red-900/15"
      title={labels.join(' · ')}
      aria-label={labels.join(', ')}
    >
      {flags.map((flag, i) => (
        <span
          key={flag}
          role="img"
          className={`${DOT_BASE} ${DOT_COLOR[flag]}`}
          aria-label={labels[i]}
        />
      ))}
      <span className="font-medium text-red-900 util-caption dark:text-red-200">
        {flags.length}
      </span>
    </span>
  );
}
